import { useState, useEffect } from 'react';
import { clients as clientsApi } from '../api/client.js';
import { ClockIcon } from '../common/icons.jsx';

const ACTION_LABELS = {
  'client.import': 'Import client',
  'item.import': 'Import unité',
  'scan.record': 'Scan enregistré',
  'signature.save': 'Signature',
  'sms.send': 'SMS envoyé',
  'stop.delivered': 'Livraison',
  'stop.problem': 'Problème signalé',
};

const ACTION_COLORS = {
  'client.import': 'bg-[#1e40af]',
  'item.import': 'bg-[#0891b2]',
  'scan.record': 'bg-[#059669]',
  'signature.save': 'bg-purple-600',
  'sms.send': 'bg-[#f97316]',
  'stop.delivered': 'bg-[#059669]',
  'stop.problem': 'bg-[#dc2626]',
};

/**
 * Audit history timeline for a client with colored dots and date formatting.
 * @param {{ clientId: string }} props
 */
export default function ClientHistory({ clientId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clientsApi.getHistory(clientId)
      .then(setHistory)
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [clientId]);

  if (loading) return <p className="text-[#64748b] text-sm py-2">Chargement historique...</p>;
  if (history.length === 0) return <p className="text-[#64748b] text-sm py-2">Aucun historique.</p>;

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <ClockIcon size={18} className="text-[#1e40af]" />
        <h3 className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Historique</h3>
      </div>
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-gray-100" />

        <div className="space-y-4">
          {history.map((entry) => {
            const dotColor = ACTION_COLORS[entry.action] || 'bg-[#64748b]';
            return (
              <div key={entry.id} className="flex items-start gap-3 relative">
                {/* Colored dot */}
                <div className={`w-3.5 h-3.5 rounded-full ${dotColor} flex-shrink-0 mt-1 ring-2 ring-white z-10`} />

                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start gap-2">
                    <span className="text-sm font-medium text-[#0f172a]">
                      {ACTION_LABELS[entry.action] || entry.action}
                    </span>
                    <span className="text-[10px] text-[#64748b] flex-shrink-0">
                      {new Date(entry.created_at).toLocaleString('fr-FR', {
                        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                  </div>
                  {entry.user_name && (
                    <p className="text-xs text-[#64748b]">par {entry.user_name}</p>
                  )}
                  {entry.details && Object.keys(entry.details).length > 0 && (
                    <p className="text-xs text-[#64748b] mt-0.5 bg-gray-50 rounded px-2 py-1 truncate">
                      {JSON.stringify(entry.details).slice(0, 120)}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
